import { jwtDecode } from 'jwt-decode';
import { AUTH_ENDPOINTS } from '../../config/AuthApiEndpoints';
import { asyncStorage } from '../storage/asyncStorageService';
 
class TokenService {
  constructor() {
    this.refreshPromise = null;
  }
 
  // Store tokens immediately to both memory cache and localStorage/sessionStorage
  setTokens(accessToken, idToken, refreshToken = null) {
    if (accessToken) {
      asyncStorage.setSync('accessToken', accessToken);
      sessionStorage.setItem('accessToken', accessToken);
 
      try {
        const decoded = jwtDecode(accessToken);
        asyncStorage.setSync('tokenExpiration', (decoded.exp * 1000).toString());
      } catch {
        // accessToken not decodeable — store 55-min fallback so expiry checks work
        asyncStorage.setSync('tokenExpiration', (Date.now() + 55 * 60 * 1000).toString());
      }
    }
 
    if (idToken) {
      asyncStorage.setSync('idToken', idToken);
      sessionStorage.setItem('idToken', idToken);
 
      try {
        const decoded = jwtDecode(idToken);
        asyncStorage.setSync('idTokenExpiration', (decoded.exp * 1000).toString());
      } catch {
        // Cognito returns a JWE (5-part encrypted token) — jwtDecode can't parse it.
        // Store 55-min fallback so the app doesn't think the token is immediately expired.
        asyncStorage.setSync('idTokenExpiration', (Date.now() + 55 * 60 * 1000).toString());
      }
    }
 
    if (refreshToken) {
      asyncStorage.setSync('refreshToken', refreshToken);
      sessionStorage.setItem('refreshToken', refreshToken);
    }
  }
 
  setToken(accessToken) {
    this.setTokens(accessToken, null, null);
  }

  setEmail(email) {
    if (email) {
      const normalized = email.toLowerCase().trim();
      asyncStorage.setSync('userEmail', normalized);
      sessionStorage.setItem('userEmail', normalized);
    }
  }
 
  getEmail() {
    return asyncStorage.get('userEmail') || sessionStorage.getItem('userEmail') || null;
  }
 
  getToken() {
    return asyncStorage.get('accessToken') || sessionStorage.getItem('accessToken') || null;
  }
 
  getIdToken() {
    return asyncStorage.get('idToken') || sessionStorage.getItem('idToken') || null;
  }
 
  async getAuthorizationToken() {
    try {
      const idToken = asyncStorage.get('idToken') || sessionStorage.getItem('idToken');
      if (idToken) return idToken;
      return asyncStorage.get('accessToken') || sessionStorage.getItem('accessToken') || null;
    } catch {
      return null;
    }
  }
 
  isAuthenticated() {
    const hasToken = !!(
      asyncStorage.get('accessToken') || sessionStorage.getItem('accessToken') ||
      asyncStorage.get('idToken') || sessionStorage.getItem('idToken')
    );
    return hasToken && !!this.getEmail();
  }
 
  isTokenExpired() {
    // Check idToken expiry first (preferred)
    const idTokenExp = asyncStorage.get('idTokenExpiration');
    if (idTokenExp) {
      return Date.now() >= (parseInt(idTokenExp) - 5 * 60 * 1000);
    }
 
    // Fall back to accessToken expiry
    const tokenExp = asyncStorage.get('tokenExpiration');
    if (tokenExp) {
      return Date.now() >= (parseInt(tokenExp) - 5 * 60 * 1000);
    }
 
    // No expiry stored — treat as valid if a token exists
    return !this.isAuthenticated();
  }
 
  // Called ONLY by the axios error interceptor on 401/403 — never proactively
  async refreshTokenIfNeeded() {
    if (this.refreshPromise) return this.refreshPromise;
 
    const refreshToken = asyncStorage.get('refreshToken') || sessionStorage.getItem('refreshToken');
    if (!refreshToken) return null;
 
    this.refreshPromise = this._doRefresh(refreshToken);
    try {
      return await this.refreshPromise;
    } finally {
      this.refreshPromise = null;
    }
  }
 
  async _doRefresh(refreshToken) {
    // Authenticate the refresh request with the REFRESH token, not the access/ID
    // token. Refresh is only ever triggered reactively (the axios interceptor calls
    // it after a request gets a 401/403), which means the access/ID tokens are
    // already expired by the time we get here. Sending the expired ID token as
    // X-Auth-Token caused the backend authorizer to reject the refresh itself —
    // so the refresh never succeeded and the user was force-logged-out at every
    // token lifetime (~3 hours). The refresh token is the correct credential here.
    const response = await fetch(AUTH_ENDPOINTS.REFRESH, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-Auth-Token': `Bearer ${refreshToken}`
      },
      body: JSON.stringify({ refreshToken })
    });
 
    if (!response.ok) throw new Error(`Token refresh failed: ${response.status}`);
 
    const data = await response.json();
    const newAccessToken = data.accessToken || data.access_token;
    const newIdToken = data.idToken || data.id_token;
    const newRefreshToken = data.refreshToken || data.refresh_token;
 
    this.setTokens(newAccessToken, newIdToken, newRefreshToken);
    return newAccessToken;
  }
 
  clearTokens() {
    ['accessToken', 'idToken', 'refreshToken', 'tokenExpiration', 'idTokenExpiration'].forEach(key => {
      asyncStorage.remove(key);
      sessionStorage.removeItem(key);
    });
  }
 
  clearAll() {
    this.clearTokens();
    asyncStorage.remove('userEmail');
    sessionStorage.removeItem('userEmail');
  }
}
 
export const tokenService = new TokenService();
 